import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  AutocompleteRenderGetTagProps,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank,
  Close,
  Info,
  Lock,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import * as API from '../../../services/API';
import {
  ModelInstance,
  Scenario,
  ScenarioEditState,
} from '../../../types/models';

import {
  downloadPromotionConflictsCsv,
  IPromotionConflict,
} from './downloadPromotionConflictsCsv';

interface Props {
  scenario: Scenario;
  availableTags?: any[];
  clientId?: number;
  modelInstance?: ModelInstance;
}

// Keeps a disabled dropdown readable: light fill, selected tags still legible.
const DISABLED_TAGS_SX = {
  '& .MuiOutlinedInput-root.Mui-disabled': { backgroundColor: 'grey.50' },
  '& .MuiChip-root.Mui-disabled': { opacity: 0.85 },
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: 'rgba(0, 0, 0, 0.7)',
  },
};

const LINK_TYPE_PROMOTION = 'Promotion';
const LINK_TYPE_SUBSCRIPTION = 'Subscription';

const TAG_NAME_MAX_LENGTH = 20;

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const renderTruncatedTags = (
  value: { id: number; Name: string }[],
  getTagProps: AutocompleteRenderGetTagProps
) =>
  value.map((option, index) => {
    const { key, ...tagProps } = getTagProps({ index });
    const fullName = option.Name;
    const isTruncated = fullName.length > TAG_NAME_MAX_LENGTH;
    const label = isTruncated
      ? `${fullName.slice(0, TAG_NAME_MAX_LENGTH)}…`
      : fullName;

    return (
      <Tooltip
        key={key}
        title={fullName}
        arrow
        disableHoverListener={!isTruncated}
      >
        <Chip {...tagProps} onDelete={undefined} size="small" label={label} />
      </Tooltip>
    );
  });

const ScenarioTagAssignment: FunctionComponent<Props> = ({
  scenario,
  availableTags = [],
  clientId,
  modelInstance,
}) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // CLD-3008: both tag dropdowns stay disabled until the instance is published.
  const isInstanceUnpublished = modelInstance
    ? !modelInstance.Published
    : false;
  // CLD-3099: and while the scenario is locked.
  const isScenarioLocked = scenario.Edit_State === ScenarioEditState.Locked;
  const isTagAssignmentDisabled = isInstanceUnpublished || isScenarioLocked;
  const getDisabledReason = (tagType: string): string =>
    isScenarioLocked
      ? `${tagType} Tags can't be changed while this scenario is locked.`
      : `${tagType} Tags are only available once this model instance is published.`;
  const [selectedPromotionTagIds, setSelectedPromotionTagIds] = useState<
    number[]
  >([]);
  const [selectedSubscriptionTagIds, setSelectedSubscriptionTagIds] = useState<
    number[]
  >([]);
  const [initialPromotionTagIds, setInitialPromotionTagIds] = useState<
    number[]
  >([]);
  const [initialSubscriptionTagIds, setInitialSubscriptionTagIds] = useState<
    number[]
  >([]);
  const [promotionTagsOpen, setPromotionTagsOpen] = useState(false);
  const [subscriptionTagsOpen, setSubscriptionTagsOpen] = useState(false);
  const [isSavingPromotion, setIsSavingPromotion] = useState(false);
  const [isSavingSubscription, setIsSavingSubscription] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadTagLinks = async (): Promise<void> => {
      try {
        const response = await API.load(
          `/instances/${scenario.ModelInstanceID}/scenarios/${scenario.id}/tag_links`
        );
        if (cancelled) return;

        const items = response?.items ?? [];
        const promotionIds = items
          .filter((item: any) => item.link_type === LINK_TYPE_PROMOTION)
          .map((item: any) => item.scenario_link_tag_id);
        const subscriptionIds = items
          .filter((item: any) => item.link_type === LINK_TYPE_SUBSCRIPTION)
          .map((item: any) => item.scenario_link_tag_id);

        setSelectedPromotionTagIds(promotionIds);
        setInitialPromotionTagIds(promotionIds);
        setSelectedSubscriptionTagIds(subscriptionIds);
        setInitialSubscriptionTagIds(subscriptionIds);
      } catch (err) {
        if (!cancelled) {
          enqueueSnackbar('Failed to load scenario tag links', {
            variant: 'error',
          });
        }
      }
    };

    loadTagLinks();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario.id, scenario.ModelInstanceID]);

  const handlePromotionTagChange = (
    event: React.SyntheticEvent,
    selectedTags: any[]
  ) => {
    const promotionTagIds = selectedTags.map((tag) => tag.id);
    setSelectedPromotionTagIds(promotionTagIds);
  };

  const handleSubscriptionTagChange = (
    event: React.SyntheticEvent,
    selectedTags: any[]
  ) => {
    const subscriptionTagIds = selectedTags.map((tag) => tag.id);
    setSelectedSubscriptionTagIds(subscriptionTagIds);
  };

  // Helper function to sort options with selected items first
  const sortOptionsWithSelectedFirst = (
    options: any[],
    selectedIds: number[]
  ): any[] => {
    if (!options || !Array.isArray(options)) return [];
    return [...options].sort((a, b) => {
      const aSelected = selectedIds.includes(a.id);
      const bSelected = selectedIds.includes(b.id);
      if (aSelected === bSelected) return 0;
      return aSelected ? -1 : 1;
    });
  };

  // CLD-3103: checks all newly added tags at once, not just the first conflict.
  const checkPromotionConflicts = async (
    tagIds: number[]
  ): Promise<IPromotionConflict[]> => {
    const results = await Promise.all(
      tagIds.map((tagId) =>
        API.load(
          `/clients/${clientId}/scenario_link_tags/${tagId}/promotion_conflict?model_instance_id=${scenario.ModelInstanceID}`
        )
      )
    );

    const conflicts: IPromotionConflict[] = [];
    results.forEach((result, index) => {
      if (!result?.hasConflict) return;
      const tagId = tagIds[index];
      conflicts.push({
        tagName: String(
          availableTags.find((t) => t.id === tagId)?.Name ?? tagId
        ),
        scenarioName: String(result.conflictingScenario?.Name ?? ''),
      });
    });
    return conflicts;
  };

  // CLD-3103: one conflict is named in the toast; several are offered as a CSV.
  const showPromotionConflicts = (conflicts: IPromotionConflict[]): void => {
    if (conflicts.length === 1) {
      const [{ tagName, scenarioName }] = conflicts;
      enqueueSnackbar(
        `Tag '${tagName}' is already promoted on scenario '${scenarioName}' in this model instance.`,
        { variant: 'error' }
      );
      return;
    }
    enqueueSnackbar(
      `${conflicts.length} Submission Tags are already promoted on other scenarios in this model instance.`,
      {
        variant: 'error',
        persist: true,
        action: (snackbarId) => (
          <>
            <Button
              size="small"
              sx={{ color: 'common.white' }}
              onClick={() => downloadPromotionConflictsCsv(conflicts)}
            >
              Download CSV
            </Button>
            <IconButton
              size="small"
              sx={{ color: 'common.white' }}
              onClick={() => closeSnackbar(snackbarId)}
            >
              <Close fontSize="small" />
            </IconButton>
          </>
        ),
      }
    );
  };

  const handleSavePromotionTags = async () => {
    try {
      setIsSavingPromotion(true);

      const newlyAddedTagIds = selectedPromotionTagIds.filter(
        (id) => !initialPromotionTagIds.includes(id)
      );
      const conflicts = await checkPromotionConflicts(newlyAddedTagIds);
      if (conflicts.length > 0) {
        showPromotionConflicts(conflicts);
        setSelectedPromotionTagIds(initialPromotionTagIds);
        return;
      }

      await API.put(
        `/instances/${scenario.ModelInstanceID}/scenarios/${scenario.id}/tag_links/promotion`,
        { tag_ids: selectedPromotionTagIds }
      );
      enqueueSnackbar('Submission tags saved successfully', {
        variant: 'success',
      });
      setInitialPromotionTagIds(selectedPromotionTagIds);
      setPromotionTagsOpen(false);
    } catch (err: any) {
      enqueueSnackbar(err?.body?.message || 'Failed to save submission tags', {
        variant: 'error',
      });
      setSelectedPromotionTagIds(initialPromotionTagIds);
    } finally {
      setIsSavingPromotion(false);
    }
  };

  const handleSaveSubscriptionTags = async () => {
    try {
      setIsSavingSubscription(true);
      await API.put(
        `/instances/${scenario.ModelInstanceID}/scenarios/${scenario.id}/tag_links/subscription`,
        { tag_ids: selectedSubscriptionTagIds }
      );
      enqueueSnackbar('Subscription tags saved successfully', {
        variant: 'success',
      });
      setInitialSubscriptionTagIds(selectedSubscriptionTagIds);
      setSubscriptionTagsOpen(false);
    } catch (err: any) {
      enqueueSnackbar(
        err?.body?.message || 'Failed to save subscription tags',
        { variant: 'error' }
      );
      setSelectedSubscriptionTagIds(initialSubscriptionTagIds);
    } finally {
      setIsSavingSubscription(false);
    }
  };

  // Custom Paper component for Submission Tags dropdown with Save button
  const PromotionTagsPaper = (props: any) => (
    <Paper {...props}>
      {props.children}
      <Box
        sx={{ p: 1, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 1 }}
      >
        <Button
          variant="contained"
          size="small"
          onMouseDown={(event) => event.preventDefault()}
          onClick={handleSavePromotionTags}
          disabled={isSavingPromotion || isTagAssignmentDisabled}
          sx={{ textTransform: 'none' }}
        >
          {isSavingPromotion ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Paper>
  );

  // Custom Paper component for Subscription Tags dropdown with Save button
  const SubscriptionTagsPaper = (props: any) => (
    <Paper {...props}>
      {props.children}
      <Box
        sx={{ p: 1, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 1 }}
      >
        <Button
          variant="contained"
          size="small"
          onMouseDown={(event) => event.preventDefault()}
          onClick={handleSaveSubscriptionTags}
          disabled={isSavingSubscription || isTagAssignmentDisabled}
          sx={{ textTransform: 'none' }}
        >
          {isSavingSubscription ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Paper>
  );

  return (
    <Card>
      <CardContent sx={{ p: 2 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Subscriptions
          </Typography>

          {isScenarioLocked && (
            <Alert
              severity="info"
              icon={<Lock fontSize="inherit" />}
              sx={{ mb: 2 }}
            >
              This scenario is locked. Submission and Subscription Tags are
              view-only until it is unlocked.
            </Alert>
          )}

          {/* Submission Tag Section */}
          <Box
            sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', gap: 2 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 150,
                pt: 1,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Submission Tags
              </Typography>
              <Tooltip
                title="Tags for submission management"
                placement="top"
                arrow
              >
                <Info
                  sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }}
                />
              </Tooltip>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Tooltip
                title={getDisabledReason('Submission')}
                placement="top"
                disableHoverListener={!isTagAssignmentDisabled}
                disableFocusListener={!isTagAssignmentDisabled}
                disableTouchListener={!isTagAssignmentDisabled}
              >
                <span>
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    sx={DISABLED_TAGS_SX}
                    disabled={isTagAssignmentDisabled}
                    open={promotionTagsOpen && !isTagAssignmentDisabled}
                    onOpen={() => setPromotionTagsOpen(true)}
                    onClose={() => setPromotionTagsOpen(false)}
                    options={sortOptionsWithSelectedFirst(
                      availableTags,
                      selectedPromotionTagIds
                    )}
                    getOptionLabel={(option) => option.Name}
                    clearIcon={null}
                    value={
                      availableTags
                        ? availableTags.filter((tag) =>
                            selectedPromotionTagIds.includes(tag.id)
                          )
                        : []
                    }
                    onChange={handlePromotionTagChange}
                    disableCloseOnSelect
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    ChipProps={{
                      size: 'small',
                      onDelete: undefined,
                    }}
                    renderTags={renderTruncatedTags}
                    renderOption={(props, option, { selected }) => (
                      <Typography component="li" {...props} noWrap>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.Name}
                      </Typography>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Submission Tags"
                        placeholder={
                          selectedPromotionTagIds.length === 0
                            ? 'Select Tags'
                            : undefined
                        }
                      />
                    )}
                    PaperComponent={PromotionTagsPaper}
                  />
                </span>
              </Tooltip>
            </Box>
          </Box>

          {/* Subscription Tag Section */}
          <Box
            sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 150,
                pt: 1,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Subscription Tags
              </Typography>
              <Tooltip
                title="Tags for subscription management"
                placement="top"
                arrow
              >
                <Info
                  sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }}
                />
              </Tooltip>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Tooltip
                title={getDisabledReason('Subscription')}
                placement="top"
                disableHoverListener={!isTagAssignmentDisabled}
                disableFocusListener={!isTagAssignmentDisabled}
                disableTouchListener={!isTagAssignmentDisabled}
              >
                <span>
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    sx={DISABLED_TAGS_SX}
                    disabled={isTagAssignmentDisabled}
                    open={subscriptionTagsOpen && !isTagAssignmentDisabled}
                    onOpen={() => setSubscriptionTagsOpen(true)}
                    onClose={() => setSubscriptionTagsOpen(false)}
                    options={sortOptionsWithSelectedFirst(
                      availableTags,
                      selectedSubscriptionTagIds
                    )}
                    getOptionLabel={(option) => option.Name}
                    clearIcon={null}
                    value={
                      availableTags
                        ? availableTags.filter((tag) =>
                            selectedSubscriptionTagIds.includes(tag.id)
                          )
                        : []
                    }
                    onChange={handleSubscriptionTagChange}
                    disableCloseOnSelect
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    ChipProps={{
                      size: 'small',
                      onDelete: undefined,
                    }}
                    renderTags={renderTruncatedTags}
                    renderOption={(props, option, { selected }) => (
                      <Typography component="li" {...props} noWrap>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.Name}
                      </Typography>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Subscription Tags"
                        placeholder={
                          selectedSubscriptionTagIds.length === 0
                            ? 'Select Tags'
                            : undefined
                        }
                      />
                    )}
                    PaperComponent={SubscriptionTagsPaper}
                  />
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ScenarioTagAssignment;
